const SENSITIVE_KEY_PATTERNS = [
  'token',
  'auth',
  'authorization',
  'cookie',
  'session',
  'email',
  'phone',
  'note',
  'comment',
  'message',
  'dietary',
  'allergy',
  'address',
  'secret',
  'password',
  'api_key',
  'apikey',
  'signing_secret',
  'access_token',
  'refresh_token',
  'invite',
  'magic_link',
];

const SENSITIVE_QUERY_PARAMS = new Set([
  'token',
  'access_token',
  'refresh_token',
  'code',
  'state',
  'guest_token',
  'invite_token',
  'auth',
  'api_key',
  'secret',
]);

export function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[-_]/g, '_');
  return SENSITIVE_KEY_PATTERNS.some((pattern) => normalized.includes(pattern));
}

export function scrubObject(value: unknown, depth = 0): unknown {
  if (depth > 5) return '[truncated]';
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map((item) => scrubObject(item, depth + 1));
  }
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, val]) => [
      key,
      isSensitiveKey(key) ? '[scrubbed]' : scrubObject(val, depth + 1),
    ]),
  );
}

export function scrubUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, 'https://tov-plus.local');
    for (const [key] of parsed.searchParams.entries()) {
      if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
        parsed.searchParams.set(key, '[scrubbed]');
      }
    }
    if (url.startsWith('/')) {
      const query = parsed.searchParams.toString();
      return `${parsed.pathname}${query ? `?${query}` : ''}`;
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

export function scrubHeaders(
  headers: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      isSensitiveKey(key) ? '[scrubbed]' : value,
    ]),
  );
}

export function truncatePayload<T extends Record<string, unknown>>(
  payload: T,
  maxValueLength = 500,
): T {
  return Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [
      key,
      typeof value === 'string' && value.length > maxValueLength
        ? `${value.substring(0, maxValueLength)}...[truncated]`
        : value,
    ]),
  ) as T;
}
